import { infiniteQueryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { api, ApiError } from './api'
import type { Space } from './families'
import { placesSaid, type Place } from './spaces'
import { isLive, nowSaid, type Shape, type Tense } from '../../shared/momentStyle'
import { showDate } from '../../shared/tree'
import type { ToldAs } from '../../shared/telling'
import type { PreparedPhoto } from './photos'

export type MomentPhoto = { id: string; width: number; height: number }
type MomentBase = {
  id: string
  familyId: string
  title: string
  // The story as everyone reads it: polished or retold when its author chose that (toldAs), else as written.
  body: string
  toldAs: ToldAs | null
  happenedOn: string | null
  // How its author tells it, or null on moments from before they could choose (shared/momentStyle.ts reads those).
  tense: Tense | null
  shape: Shape | null
  createdAt: number
  sharedAt: number | null
  author: { id: string; name: string; portrait: string | null }
  // Shared with Everyone (missing from an app that loaded before Everyone); every place it's shared with that you're
  // in, in any of your circles (neither while it's kept just for its author); and the first of those in the circle
  // it's seen from.
  everyone?: boolean
  sharedWith: Place[]
  space: Space | null
  mine: boolean
}
// As a list shows it: the story cut short where it's long (more), and the first few photos of photoCount.
export type MomentSummary = MomentBase & { more: boolean; photos: MomentPhoto[]; photoCount: number }
// Someone tagged as there, as far as you know them (on the tree of a circle you're in).
export type MomentPerson = { id: string; name: string; living: boolean; pet: boolean; portrait: string | null }
// written is the story exactly as its author wrote it, sent only to them and only when it's polished or retold.
export type Moment = MomentBase & { photos: MomentPhoto[]; people: MomentPerson[]; written?: string }
// A polished or retold version of a story, as saved with it.
export type Told = { as: ToldAs; text: string }
export type MomentFields = {
  title: string
  body: string
  happenedOn: string | null
  // Who sees it: Everyone, or these places (neither: only its author).
  everyone: boolean
  spaceIds: string[]
  // Who was there, by their ids on the tree.
  people: string[]
  // How everyone else reads it, or null for as written.
  told: Told | null
  tense: Tense
  shape: Shape | null
}
type MomentsPage = { moments: MomentSummary[]; next: number | null }
// What a list can narrow to: happening now or memories, someone tagged, a decade of memories ("1990").
export type MomentFilters = { tense?: Tense; person?: string; decade?: string; year?: string }
// Happening now: each person's recent Now moments, in the order they play.
export type NowPerson = { author: MomentSummary['author']; moments: MomentSummary[]; latest: number }
export type RecentMoments = { since: number; count: number; moments: MomentSummary[] }

// The most photos one moment holds; more belong in an album.
export const PHOTOS_PER_MOMENT = 12

export const momentsQuery = (familyId: string, mine: boolean, spaceId?: string, filters: MomentFilters = {}) =>
  infiniteQueryOptions({
    queryKey: [
      'moments',
      familyId,
      mine,
      spaceId ?? 'all',
      filters.tense ?? '',
      filters.person ?? '',
      filters.decade ?? '',
      filters.year ?? '',
    ],
    initialPageParam: 0,
    getNextPageParam: (last: MomentsPage) => last.next,
    queryFn: ({ pageParam }) => {
      const query = new URLSearchParams({ page: String(pageParam) })
      if (mine) query.set('mine', '1')
      if (spaceId) query.set('space', spaceId)
      if (filters.tense) query.set('tense', filters.tense)
      if (filters.person) query.set('person', filters.person)
      if (filters.decade) query.set('decade', filters.decade)
      if (filters.year) query.set('year', filters.year)
      return api<MomentsPage>(`/families/${familyId}/moments?${query}`)
    },
  })

export const useMoments = (
  familyId: string,
  mine: boolean,
  spaceId?: string,
  filters: MomentFilters = {},
  enabled = true,
) => useInfiniteQuery({ ...momentsQuery(familyId, mine, spaceId, filters), enabled })

// Moments in rows (Circles and People): a row for each circle you're in (each space, on a circle's own pages), or for
// each person who wrote any, with its newest few as they were posted.
export type MomentShelf = {
  key: string
  kind: 'circle' | 'space' | 'person'
  name: string
  color?: string | null
  author?: MomentSummary['author']
  moments: MomentSummary[]
  more: boolean
}
export const useMomentShelves = (
  familyId: string,
  by: 'circles' | 'people',
  mine: boolean,
  spaceId?: string,
  filters: Pick<MomentFilters, 'tense' | 'person'> = {},
  enabled = true,
) =>
  useQuery({
    enabled,
    queryKey: ['moments', familyId, 'shelves', by, mine, spaceId ?? 'all', filters.tense ?? '', filters.person ?? ''],
    queryFn: () => {
      const query = new URLSearchParams({ by })
      if (mine) query.set('mine', '1')
      if (spaceId) query.set('space', spaceId)
      if (filters.tense) query.set('tense', filters.tense)
      if (filters.person) query.set('person', filters.person)
      return api<{ shelves: MomentShelf[] }>(`/families/${familyId}/moments/shelves?${query}`)
    },
  })

export const useRecentMoments = (
  familyId: string,
  options: { since?: number; spaceId?: string; enabled?: boolean } = {},
) =>
  useQuery({
    enabled: options.enabled ?? true,
    queryKey: ['moments', familyId, 'recent', options.since ?? null, options.spaceId ?? 'all'],
    queryFn: () => {
      const query = new URLSearchParams()
      if (options.since !== undefined) query.set('since', String(options.since))
      if (options.spaceId) query.set('space', options.spaceId)
      return api<RecentMoments>(`/families/${familyId}/moments/recent?${query}`)
    },
  })

// Happening now, for the rings across the top of Modern moments.
export const useNowMoments = (familyId: string, spaceId?: string, enabled = true) =>
  useQuery({
    enabled,
    queryKey: ['moments', familyId, 'now', spaceId ?? 'all'],
    queryFn: () =>
      api<{ people: NowPerson[] }>(
        `/families/${familyId}/moments/now${spaceId ? `?space=${encodeURIComponent(spaceId)}` : ''}`,
      ),
  })

export const useMoment = (id: string) =>
  useQuery({ queryKey: ['moment', id], queryFn: () => api<Moment>(`/moments/${encodeURIComponent(id)}`) })

// A story polished or retold, to show its author before they save it. Refused (422) when it couldn't keep their words.
export const tellStory = (familyId: string, text: string, as: ToldAs) =>
  api<Told>(`/families/${familyId}/moments/tell`, { method: 'POST', body: { text, as } })

export const momentPhotoUrl = (id: string, variant: 'original' | 'display' | 'medium' | 'thumb') =>
  `/api/moment-photos/${id}/${variant}`

export async function uploadMomentPhoto(momentId: string, photo: PreparedPhoto) {
  const form = new FormData()
  form.set('original', photo.original)
  form.set('display', photo.display, 'display')
  if (photo.medium) form.set('medium', photo.medium, 'medium')
  form.set('thumb', photo.thumb, 'thumb')
  form.set('width', String(photo.width))
  form.set('height', String(photo.height))
  const res = await fetch(`/api/moments/${momentId}/photos`, { method: 'POST', body: form, credentials: 'same-origin' })
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string }
    throw new ApiError(res.status, data.error ?? 'The photo didn’t upload. Please try again.')
  }
  return ((await res.json()) as { id: string }).id
}

// Who sees a moment, in words: its space's name, or "Only you" while it's kept just for its author.
// Who sees a moment, in a few words. In a list it's who sees it in the circle you're looking at; `whole` says every
// place you know it's shared, naming the circles when there's more than one.
export function audience(
  moment: Pick<MomentBase, 'space'> & { sharedWith?: Place[]; everyone?: boolean },
  whole = false,
) {
  if (moment.everyone) return 'Everyone'
  const shared = moment.sharedWith ?? []
  if (moment.space && !whole) return moment.space.name
  if (shared.length) return placesSaid(shared, whole || new Set(shared.map((x) => x.familyId)).size > 1)
  return moment.space ? moment.space.name : 'Only you'
}

// Kept just for its author: shared with no one.
export const isPrivate = (moment: Pick<MomentBase, 'space'> & { sharedWith?: Place[]; everyone?: boolean }) =>
  !moment.everyone && !moment.space && !moment.sharedWith?.length

// A story's paragraphs, as written: a blank line starts a new one.
export const paragraphs = (text: string) =>
  text
    .split(/\n\s*\n/)
    .map((x) => x.trim())
    .filter(Boolean)

// When something was written or shared, as a person would say it: "12 Oct", or "12 Oct 2025" from another year.
export function written(ms: number, today = new Date()) {
  const date = new Date(ms)
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' }),
  })
}

export type ToldFields = Pick<MomentSummary, 'tense' | 'shape' | 'happenedOn' | 'createdAt' | 'body' | 'photoCount'>

// When a moment is from, in words, the way Modern pages say it. A Now moment that's still live: "Now · Tonight". One
// that isn't any more was written at the time: "Written at the time · 16 Oct 2026". A memory: its date, or nothing
// when its author didn't give one.
export function toldWhen(m: ToldFields, now = new Date()) {
  if (isLive(m, now.getTime())) return { live: true, text: `Now · ${nowSaid(m.createdAt, now)}` }
  if (m.tense === 'now') {
    const day = new Date(m.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    return { live: false, text: `Written at the time · ${day}` }
  }
  return { live: false, text: m.happenedOn ? showDate(m.happenedOn) : '' }
}

// Things dealt into n columns in turn, as cards are dealt, each keeping where it was in the list: the first n make the
// top row, left to right, so a wall still reads newest first.
export function dealColumns<T>(items: T[], n: number): { item: T; index: number }[][] {
  const columns: { item: T; index: number }[][] = Array.from({ length: Math.max(1, n) }, () => [])
  items.forEach((item, index) => columns[index % columns.length]!.push({ item, index }))
  return columns
}
